import { PurchaseOrderLookup } from './model';

export type ReceivingStatusKind = 'not_received' | 'partial' | 'received_uncounted' | 'full' | 'full_untracked';

export interface ReceivingStatus {
  kind: ReceivingStatusKind;
  text: string;
  color: string;
}

const BLUE = '#1976d2';
const ORANGE = '#ed6c02';
const RED = '#d32f2f';

// Quantities come through as numbers (and sometimes 4.0); the banner must not print a trailing .0.
export const formatQty = (value: number): string => `${Number(value)}`;

// First-match-wins table from spec §5.2 + C2/C3. (0, 0) must not say "0 of 0"; a flagged PO with
// received 0 must not say "0 of Y, FULLY RECEIVED". Ordered 0 has no denominator, so a flagged PO
// that did receive something says "X received" rather than "X of 0" — the same shapes as the log.
export const getReceivingStatus = (po: PurchaseOrderLookup): ReceivingStatus => {
  const received = Number(po.received_quantity);
  const ordered = Number(po.ordered_quantity);

  if (po.fully_received && received === 0) {
    return { kind: 'full_untracked', text: 'FULLY RECEIVED — count not tracked', color: RED };
  }

  if (ordered === 0 && received > 0) {
    if (po.fully_received) {
      return { kind: 'full', text: `FULLY RECEIVED — ${formatQty(received)} received`, color: RED };
    }

    return { kind: 'received_uncounted', text: `RECEIVED — ${formatQty(received)} received`, color: ORANGE };
  }

  if (po.fully_received || (ordered > 0 && received >= ordered)) {
    return {
      kind: 'full',
      text: `FULLY RECEIVED — ${formatQty(received)} of ${formatQty(ordered)} received`,
      color: RED,
    };
  }

  if (received === 0) {
    return {
      kind: 'not_received',
      text: ordered === 0 ? 'NOT RECEIVED' : `NOT RECEIVED — 0 of ${formatQty(ordered)}`,
      color: BLUE,
    };
  }

  return {
    kind: 'partial',
    text: `PARTIALLY RECEIVED — ${formatQty(received)} of ${formatQty(ordered)} received`,
    color: ORANGE,
  };
};

// C2: ordered 0 is unmeasurable, so never confirm. Flagged POs always confirm (C3), even at count 0.
export const needsReceiveConfirmation = (po: PurchaseOrderLookup, count: number): boolean => {
  if (po.fully_received) return true;

  const ordered = Number(po.ordered_quantity);
  const received = Number(po.received_quantity);
  return ordered > 0 && received + count > ordered;
};
